import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ScreenLoaderComponent } from '../../../shared/components/load/screen-loader.component';
import { provideNativeDateAdapter, DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { MatDialog } from '@angular/material/dialog';

registerLocaleData(localeEs);
import { Router } from '@angular/router';
import { EmergencyContactService } from '../services/emergency-contact.service';
import { UserService } from '../services/user.service';
import { EmergencyContact } from '../models/emergency-contact.model';
import { NotificationModalComponent } from '../../../shared/components/notification-modal/notification-modal.component';
import { LoaderService } from '../../../shared/services/loader.service';

@Component({
  selector: 'app-manage-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, ScreenLoaderComponent],
  templateUrl: './manage-profile.component.html',
  styleUrls: ['./manage-profile.component.scss'],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' }
  ]
})
export class ManageProfileComponent implements OnInit {
  isEditingMode = false;
  isEditingPhone = false;
  isEditingAdditionalPhone = false;
  isEditingAdditionalEmail = false;
  isEditingEmergencyName = false;
  isEditingEmergencyEmail = false;
  isEditingEmergencyPhone = false;
  isEditingFullName = false;
  isEditingBirthDate = false;
  showAdditionalPhone = false;
  showAdditionalEmail = false;

  emergencyContacts: EmergencyContact[] = [];
  currentEmergencyContact: EmergencyContact | null = null;

  userPhone = {
    primary: '',
    additional: ''
  };

  userEmail = {
    primary: '',
    additional: ''
  };

  userPersonalInfo = {
    fullName: '',
    birthDate: '' as string | Date
  };

  contactMethod = 'email';
  maxBirthDate: Date;

  get isLoading() {
    return this.loaderService.loading$;
  }

  constructor(
    private router: Router,
    private emergencyContactService: EmergencyContactService,
    private userService: UserService,
    private dateAdapter: DateAdapter<Date>,
    private dialog: MatDialog,
    private loaderService: LoaderService
  ) {
    // Configurar locale para el datepicker
    this.dateAdapter.setLocale('es-ES');

    // Calcular fecha máxima (18 años atrás desde hoy)
    const today = new Date();
    this.maxBirthDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  }

  ngOnInit(): void {
    this.loadUserContactData();
    this.loadEmergencyContacts();
  }

  private loadUserContactData(): void {
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    if (user) {
      this.userEmail.primary = user.email || '';
      this.userEmail.additional = user.additionalemail || '';
      this.userPhone.primary = user.phone || '';
      this.userPhone.additional = user.additionalphone || '';
      this.userPersonalInfo.fullName = user.name || '';
      this.userPersonalInfo.birthDate = this.formatDateForInput(user.birthDate || user.birthdate || '');

      this.showAdditionalPhone = !!(user.additionalphone && user.additionalphone !== '0');
      this.showAdditionalEmail = !!user.additionalemail;

      // Cargar método de contacto
      if (user.contactMeans) {
         const contactMeansMap: { [key: string]: string } = {
                  '1': 'email',
                  '2': 'whatsapp',
                  '3': 'both'
                };
                this.contactMethod = contactMeansMap[user.contactMeans] || 'email';
      }
      if (!user.contactMeans && user.contactmeans) {
        const contactMeansMap: { [key: string]: string } = {
          '1': 'email',
          '2': 'whatsapp',
          '3': 'both'
        };
        this.contactMethod = contactMeansMap[user.contactmeans.id] || 'email';
      }
    }
  }

  togglePhoneEdit() {
    this.isEditingPhone = !this.isEditingPhone;
  }

  toggleAdditionalPhoneEdit() {
    this.isEditingAdditionalPhone = !this.isEditingAdditionalPhone;
  }

  toggleAdditionalEmailEdit() {
    this.isEditingAdditionalEmail = !this.isEditingAdditionalEmail;
  }

  toggleEmergencyNameEdit() {
    this.isEditingEmergencyName = !this.isEditingEmergencyName;
  }

  toggleEmergencyEmailEdit() {
    this.isEditingEmergencyEmail = !this.isEditingEmergencyEmail;
  }

  toggleEmergencyPhoneEdit() {
    this.isEditingEmergencyPhone = !this.isEditingEmergencyPhone;
  }

  toggleFullNameEdit() {
    this.isEditingFullName = !this.isEditingFullName;
  }

  toggleBirthDateEdit() {
    this.isEditingBirthDate = !this.isEditingBirthDate;
  }

  setContactMethod(method: string) {
    if (this.isEditingMode) {
      this.contactMethod = method;
    }
  }

  private getContactMethodId(method: string): string {
    const methodMap: { [key: string]: string } = {
      'email': '1',
      'whatsapp': '2',
      'both': '3'
    };
    return methodMap[method];
  }

  saveContactMethodChanges() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const cognitoId = user?.cognito;

    if (cognitoId) {
      const contactMeansId = this.getContactMethodId(this.contactMethod);

      this.userService.updateContactMethod(cognitoId, contactMeansId).subscribe({
        next: (response: any) => {
          if (response.success) {
            user.contactMeans = contactMeansId;
            localStorage.setItem('user', JSON.stringify(user));
          }
        },
        error: (error: any) => console.error('Error updating contact method:', error)
      });
    }
  }

  addAdditionalPhone() {
    this.showAdditionalPhone = true;
    this.isEditingAdditionalPhone = true;
  }

  addAdditionalEmail() {
    this.showAdditionalEmail = true;
    this.isEditingAdditionalEmail = true;
  }

  savePhoneChanges() {
    this.isEditingPhone = false;
    if (this.userPhone.primary) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const cognitoId = user?.cognito;

      if (cognitoId) {
        this.userService.updatePhone(cognitoId, Number(this.userPhone.primary)).subscribe({
          next: (response: any) => {
            if (response.success) {
              user.phone = this.userPhone.primary;
              localStorage.setItem('user', JSON.stringify(user));
              this.loadUserContactData();
            }
          },
          error: (error: any) => console.error('Error updating phone:', error)
        });
      }
    }
  }

  saveAdditionalPhoneChanges() {
    this.isEditingAdditionalPhone = false;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const cognitoId = user?.cognito;

    if (cognitoId) {
      const additionalPhone = this.userPhone.additional ? Number(this.userPhone.additional) : 0;
      this.userService.updateAdditionalPhone(cognitoId, additionalPhone).subscribe({
        next: (response: any) => {
          if (response.success) {
            user.additionalphone = this.userPhone.additional;
            localStorage.setItem('user', JSON.stringify(user));
            this.loadUserContactData();
          }
        },
        error: (error: any) => console.error('Error updating additional phone:', error)
      });
    }
  }

  saveAdditionalEmailChanges() {
    this.isEditingAdditionalEmail = false;
    if (this.userEmail.additional) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const cognitoId = user?.cognito;

      if (cognitoId) {
        this.userService.updateAdditionalEmail(cognitoId, this.userEmail.additional).subscribe({
          next: (response: any) => {
            if (response.success) {
              user.additionalemail = this.userEmail.additional;
              localStorage.setItem('user', JSON.stringify(user));
              this.loadUserContactData();
            }
          },
          error: (error: any) => console.error('Error updating additional email:', error)
        });
      }
    }
  }

  private loadEmergencyContacts(): void {
    const res = localStorage.getItem('user');
    const user = res ? JSON.parse(res) : null;
    const cognitoId = user?.cognito;

    if (!cognitoId) return;

    this.emergencyContactService.getEmergencyContacts(cognitoId).subscribe({
      next: (response) => {
        if (response.success && response.data && response.data.length > 0) {
          this.emergencyContacts = response.data;
          this.currentEmergencyContact = this.emergencyContacts[0];
        } else {
          this.initializeEmptyContact();
        }
      },
      error: () => {
        this.initializeEmptyContact();
      }
    });
  }

  private initializeEmptyContact(): void {
    this.currentEmergencyContact = {
      contact: 0,
      name: '',
      pref: '+52',
      phone: '',
      email: '',
      active: true,
      creation: ''
    };
  }

  saveEmergencyNameChanges() {
    if (this.currentEmergencyContact) {
      if (this.currentEmergencyContact.contact === 0) {
        this.createEmergencyContact();
      } else {
        this.updateEmergencyContact();
      }
    }
    this.isEditingEmergencyName = false;
  }

  saveEmergencyEmailChanges() {
    if (this.currentEmergencyContact) {
      if (this.currentEmergencyContact.contact === 0) {
        this.createEmergencyContact();
      } else {
        this.updateEmergencyContact();
      }
    }
    this.isEditingEmergencyEmail = false;
  }

  saveEmergencyPhoneChanges() {
    if (this.currentEmergencyContact) {
      if (this.currentEmergencyContact.contact === 0) {
        this.createEmergencyContact();
      } else {
        this.updateEmergencyContact();
      }
    }
    this.isEditingEmergencyPhone = false;
  }

  private createEmergencyContact(): void {
    if (!this.currentEmergencyContact) return;

    const res = localStorage.getItem('user');
    const user = res ? JSON.parse(res) : null;
    const cognitoId = user?.cognito;

    if (!cognitoId) return;

    this.emergencyContactService.createEmergencyContact(
      cognitoId,
      this.currentEmergencyContact.name,
      this.currentEmergencyContact.pref,
      this.currentEmergencyContact.phone,
      this.currentEmergencyContact.email
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.loadEmergencyContacts();
        }
      },
      error: () => {}
    });
  }

  private updateEmergencyContact(): void {
    if (!this.currentEmergencyContact) return;

    const res = localStorage.getItem('user');
    const user = res ? JSON.parse(res) : null;
    const cognitoId = user?.cognito;

    if (!cognitoId) return;

    this.emergencyContactService.updateEmergencyContact(
      cognitoId,
      this.currentEmergencyContact.contact,
      this.currentEmergencyContact.name,
      this.currentEmergencyContact.pref,
      this.currentEmergencyContact.phone,
      this.currentEmergencyContact.email
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.loadEmergencyContacts();
        }
      },
      error: () => {}
    });
  }

  requestPasswordChange() {
    this.router.navigate(['/cambio-contrasena']);
  }

  saveFullNameChanges() {
    this.isEditingFullName = false;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const cognitoId = user?.cognito;

    if (cognitoId) {
      this.userService.updateUserInfo(cognitoId, { fullName: this.userPersonalInfo.fullName }).subscribe({
        next: (response: any) => {
          if (response.success) {
            user.name = this.userPersonalInfo.fullName;
            localStorage.setItem('user', JSON.stringify(user));
          }
        },
        error: (error: any) => console.error('Error updating full name:', error)
      });
    }
  }

  saveBirthDateChanges() {
    this.isEditingBirthDate = false;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const cognitoId = user?.cognito;

    if (cognitoId) {
      // Convertir Date object a string YYYY-MM-DD
      const dateString = this.formatDateToString(this.userPersonalInfo.birthDate);

      this.userService.updateUserInfo(cognitoId, { birthdate: dateString }).subscribe({
        next: (response: any) => {
          if (response.success) {
            // Guardar en localStorage en formato DDMMYYYY
            const formattedDate = this.formatDateToDDMMYYYY(dateString);
            user.birthdate = formattedDate;
            user.birthDate = formattedDate;
            localStorage.setItem('user', JSON.stringify(user));
          }
        },
        error: (error: any) => console.error('Error updating birth date:', error)
      });
    }
  }

  private formatDateForInput(dateString: string): Date | string {
    if (!dateString) return '';

    // Si la fecha viene en formato YYYY-MM-DD HH:mm:ss, extraer solo la fecha
    if (dateString.includes(' ')) {
      dateString = dateString.split(' ')[0];
    }

    // Si viene en formato DDMMYYYY, convertir a Date object para evitar problemas de zona horaria
    if (dateString.length === 8 && !dateString.includes('-')) {
      const day = parseInt(dateString.substring(0, 2));
      const month = parseInt(dateString.substring(2, 4)) - 1; // Los meses en Date van de 0-11
      const year = parseInt(dateString.substring(4, 8));
      return new Date(year, month, day);
    }

    // Si ya está en formato YYYY-MM-DD, convertir a Date object
    if (dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1; // Los meses en Date van de 0-11
        const day = parseInt(parts[2]);
        return new Date(year, month, day);
      }
    }

    return dateString;
  }

  private formatDateToDDMMYYYY(dateString: string): string {
    if (!dateString) return '';

    // Si viene en formato YYYY-MM-DD, convertir a DDMMYYYY
    if (dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[2]}${parts[1]}${parts[0]}`;
      }
    }

    return dateString;
  }

  private formatDateToString(date: any): string {
    if (!date) return '';

    // Si es un objeto Date, convertir a YYYY-MM-DD
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    // Si ya es string, devolverlo tal como está
    return date;
  }

  getFormattedBirthDate(): string {
    if (!this.userPersonalInfo.birthDate) return '';

    // Si es un objeto Date, convertir a DD/MM/YYYY
    if (this.userPersonalInfo.birthDate instanceof Date) {
      const year = this.userPersonalInfo.birthDate.getFullYear();
      const month = String(this.userPersonalInfo.birthDate.getMonth() + 1).padStart(2, '0');
      const day = String(this.userPersonalInfo.birthDate.getDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    }

    // Si está en formato YYYY-MM-DD, convertir a DD/MM/YYYY para mostrar
    if (typeof this.userPersonalInfo.birthDate === 'string' && this.userPersonalInfo.birthDate.includes('-')) {
      const parts = this.userPersonalInfo.birthDate.split('-');
      if (parts.length === 3) {
        const day = parts[2].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[0];
        return `${day}/${month}/${year}`;
      }
    }

    return String(this.userPersonalInfo.birthDate);
  }

  formatDateInput(event: any): void {
    let value = event.target.value.replace(/\D/g, '');

    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }

    event.target.value = value;

    // Actualizar el modelo Date si la fecha está completa
    if (value.length === 10) {
      const parts = value.split('/');
      const day = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1; // Mes en formato 0-11
      const year = parseInt(parts[2]);

      if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900) {
        this.userPersonalInfo.birthDate = new Date(year, month, day);
      }
    }
  }

  soloNumeros(event: KeyboardEvent): void {
    const charCode = event.key.charCodeAt(0);
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  soloLetras(event: KeyboardEvent): void {
    const char = event.key;
    // Permitir letras, espacios, tildes y caracteres especiales del español
    const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]$/;

    // Permitir teclas especiales (backspace, delete, flechas, etc.)
    if (char.length === 1 && !regex.test(char)) {
      event.preventDefault();
    }
  }

  toggleEditMode() {
    this.isEditingMode = !this.isEditingMode;
    if (this.isEditingMode) {
      this.isEditingFullName = true;
      this.isEditingBirthDate = true;
      this.isEditingPhone = true;
    } else {
      this.isEditingFullName = false;
      this.isEditingBirthDate = false;
      this.isEditingPhone = false;
    }
  }

  saveAllChanges() {
    // Validar teléfono principal
    if (!this.userPhone.primary || this.userPhone.primary.length !== 10) {
      console.error('El teléfono principal debe tener exactamente 10 dígitos');
      return;
    }

    // Validar nombre completo
    if (!this.userPersonalInfo.fullName || this.userPersonalInfo.fullName.trim().length < 2) {
      console.error('El nombre completo es obligatorio y debe tener al menos 2 caracteres');
      return;
    }

    // Validar fecha de nacimiento
    if (!this.userPersonalInfo.birthDate) {
      console.error('La fecha de nacimiento es obligatoria');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const cognitoId = user?.cognito;

    if (cognitoId) {
      // Mostrar loader
      this.loaderService.show();

      const dateString = this.formatDateToString(this.userPersonalInfo.birthDate);
      const contactMeansId = this.getContactMethodId(this.contactMethod);

      const updateData = {
        fullName: this.userPersonalInfo.fullName,
        birthdate: dateString,
        mainPhone: Number(this.userPhone.primary),
        contactMeans: contactMeansId
      };

      this.userService.updateUserInfo(cognitoId, updateData).subscribe({
        next: (response: any) => {
          this.loaderService.hide();
          if (response.success) {
            const formattedDate = this.formatDateToDDMMYYYY(dateString);
            user.name = this.userPersonalInfo.fullName;
            user.birthdate = formattedDate;
            user.birthDate = formattedDate;
            user.phone = this.userPhone.primary;
            user.contactMeans = contactMeansId;
            localStorage.setItem('user', JSON.stringify(user));

            // Mostrar modal de éxito
            const dialogRef = this.dialog.open(NotificationModalComponent, {
              data: {
                title: 'Éxito',
                message: 'La información se ha actualizado correctamente.',
                type: 'success'
              }
            });

            dialogRef.afterClosed().subscribe(() => {
              // Refrescar la página
              window.location.reload();
            });
          }
        },
        error: (error: any) => {
          this.loaderService.hide();
          console.error('Error updating user info:', error);
          // Mostrar modal de error
          this.dialog.open(NotificationModalComponent, {
            data: {
              title: 'Error',
              message: 'Ocurrió un error al actualizar la información. Inténtalo de nuevo.',
              type: 'error'
            }
          });
        }
      });
    }

    this.isEditingFullName = false;
    this.isEditingBirthDate = false;
    this.isEditingPhone = false;
    this.isEditingMode = false;
  }
}
